import { InputStructureProps } from "../../../Reusables/components/CygnusFormBuilder/CygnusFormBuilder.utils";
import { FieldProps } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import { fetchForms } from "./ClientCreator.service";
import { makeCygnusApiCall } from "../../../api/api";
import { fetchClientData } from "../ClientEditor/ClientEditor.service";
import { PurposeOption } from "../ClientsGrid/ClientsGrid.utils";
import { SYNTHETIC_RULE_FIELD_CODES } from "./ClientCreator.types";

export interface FormRecord {
  FirmFormId: number;
  FirmId: number;
  FirmFormsTemplateId: number;
  FormName: string;
  FormDescription: string;
  FormPurpose: string;
  FormTypeCode: string;
  InputStructure: InputStructureProps;
  Url: string;
  ClientTypeCode: string;
  ClientTypeName: string;
  CreatedDate: string;
  UpdatedDate: string;
  CreatedBy: string;
  UpdatedBy: string;
}

export interface ClientCreatorProps {
  show: boolean;
  refresh: () => void;
  onHide: () => void;
  purpose: PurposeOption | null;
}


export type FormField = {
  code: string;
  label: string;
  value: string | boolean;
};

export const stripSyntheticRuleFields = (fields: FieldProps[]): FieldProps[] =>
  fields.filter((f) => !SYNTHETIC_RULE_FIELD_CODES.includes((f as any).allfields_code));

const ALLOWED_SELECT_META_KEYS = ["email", "position", "location"] as const;

/**
 * Loads all client forms from the server and categorizes them dynamically
 * based on their FormPurpose as defined in the formFlowConfig.
 *
 * @param formFlowConfig - Configuration of tabs with mapping to FormPurpose
 * @param setClientRecordsByTab - Setter to update records per tab key
 * @param setFormsApiProgress - Setter to toggle loading state
 */
export const loadClientForms = async (
  formFlowConfig: {
    key: string;
    label: string;
    idField: string;
    enabled: boolean;
    order: number;
  }[],
  setClientRecordsByTab: React.Dispatch<
    React.SetStateAction<Record<string, FormRecord[]>>
  >,
  setFormsApiProgress: React.Dispatch<React.SetStateAction<boolean>>
) => {
  try {
    setFormsApiProgress(true);
    const records = await fetchForms();

    // Build a map of tab key → filtered forms
    const categorizedRecords: Record<string, FormRecord[]> = {};

    formFlowConfig.forEach(({ key, label }) => {
      categorizedRecords[key] = records.filter((record: FormRecord) =>
        record.FormTypeCode?.toLowerCase().includes(key.toLowerCase())
      );
    });

    setClientRecordsByTab((prev) => ({
      ...prev,
      ...categorizedRecords,
    }));

    // setFormsApiProgress(false);
  } catch (error) {
    console.error("Error fetching client form records:", error);
    // setFormsApiProgress(false);
  }
};

/**
 * Resets all relevant state and calls onHide to close the modal.
 *
 * @param setCurrentPage - Sets wizard step to 0 (onboarding)
 * @param setClientOnboardingRecords - Clears onboarding records
 * @param setClientAcceptanceRecords - Clears acceptance records
 * @param setSelectedOnboardingRecord - Resets onboarding form selection
 * @param setClientOnboardingFormFields - Clears form field structure
 * @param setIsPublic - Resets isPublic toggle
 * @param setSelectedPurposes - Clears selected purposes
 * @param onHide - Calls parent close function
 */
export const handleClose = (
  resetCurrentPage: () => void,
  resetClientRecordsByTab: () => void,
  refresh: () => void,
  onHide: () => void
) => {
  resetCurrentPage();
  resetClientRecordsByTab();
  onHide();
  refresh();
};

const capitalize = (s: string) =>
  s && s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/**
 * Submits updated client form data to the API.
 *
 * @param setClientUpdateApiProgress - Sets loading spinner state
 * @param clientFormFields - List of form fields with label/value
 * @param clientMasterId - ID of the client being edited
 * @param formUrl - API endpoint for form submission
 * @param formId - ID of the form being used
 * @param close - Callback to close modal/view after success
 */
export const updateClientData = async (
  setClientUpdateApiProgress: (arg: boolean) => void,
  clientFormFields: FieldProps[],
  clientMasterId: number,
  formUrl: string,
  formId: number,
  activeTab: any,
  enableNextTab: (currentTab: any) => void
) => {
  setClientUpdateApiProgress(true);

  const visibleFields = normalizeClientFieldValues(stripSyntheticRuleFields(clientFormFields));

  const formFields: FormField[] = buildFormFields(visibleFields);

  // console.log("IN UPDATE -------", activeTab);
  try {
    const response = await makeCygnusApiCall({
      type: "PUT",
      payload: {
        ClientFormFields: formFields,
        FormId: formId,
        ClientMasterId: clientMasterId,
      },
      url: `/forms/${formUrl}/`,
      show_message: true,
      customized_success_message: "Prospect updated successfully.",
      customized_failure_message:
        "Unable to update your prospect, please try again. If the problem persists, please contact support and attach a screenshot.",
      customized_error_message:
        "Error occured while updating the prospect details, please contact support and attach a screenshot.",
      isblob: false,
      forMDM: true,
    });
    if (response.success) {
      enableNextTab(activeTab);
      // close();
      // DO NOT CLOSE BY DEFAULT SINCE WE HAVE MULTIPLE TABS TO WORK WITH
    }
  } catch (error) {
    console.warn("Error occurred while updating client.");
  } finally {
    setClientUpdateApiProgress(false);
  }
};

const isDependencySatisfied = (
  child: FieldProps,
  byCode: Map<string, FieldProps>
): boolean => {
  const parentCode = String((child as any).dependsOn || "").trim();
  if (!parentCode) return true;

  const parent = byCode.get(parentCode);
  if (!parent) return true; // fail open

  const parentValue = (parent as any).value;

  const allowedList = Array.isArray((child as any).dependsOnVisibilityValues)
    ? (child as any).dependsOnVisibilityValues
    : [];

  const allowedSingle = (child as any).dependsOnVisibilityValue;
  const allowed = allowedList.length
    ? allowedList
    : typeof allowedSingle !== "undefined"
      ? [allowedSingle]
      : [];

  if (!allowed.length) return true;

  return allowed.some((a: any) => looselyEqual(parentValue, a));
};

const looselyEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;

  const na = normalizeBoolish(a);
  const nb = normalizeBoolish(b);
  if (typeof na === "boolean" && typeof nb === "boolean") return na === nb;

  return (
    String(a ?? "").trim().toLowerCase() ===
    String(b ?? "").trim().toLowerCase()
  );
};

const normalizeBoolish = (v: unknown): boolean | undefined => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;

  const s = String(v ?? "").trim().toLowerCase();
  if (["true", "1", "yes", "y", "on"].includes(s)) return true;
  if (["false", "0", "no", "n", "off"].includes(s)) return false;

  return undefined;
};

const toBoolean = (input: unknown): boolean => {
  const n = normalizeBoolish(input);
  return typeof n === "boolean" ? n : Boolean(input);
};

/**
 * applyCheckboxDefaultsBeforeSave (PURE)
 *
 * Rules (checkbox only):
 * 1) If value exists (including false), do not touch it.
 * 2) If value is "" | null | undefined and defaultValue exists, set value from defaultValue.
 * 3) If both value and defaultValue are missing/empty, set value = false.
 */
export const applyCheckboxDefaultsBeforeSave = (fields: FieldProps[]): FieldProps[] => {
  const byCode = new Map<string, FieldProps>();
  fields.forEach((f) => {
    const code = (f as any).allfields_code;
    if (code) byCode.set(code, f);
  });

  return fields.map((f) => {
    if (f.type !== "checkbox") return f;

    const v = (f as any).value;
    const hasRealValue =
      typeof v === "boolean" || (typeof v === "string" && v.trim() !== "");

    const isDependent = Boolean((f as any).dependsOn);
    const dependencySatisfied = !isDependent || isDependencySatisfied(f, byCode);

    // CASE 0: Parent NOT satisfied
    if (!dependencySatisfied) {
      // If value is empty/null → force false
      if (!hasRealValue) {
        return { ...f, value: false };
      }
      // If value exists → leave it
      return f;
    }

    // CASE 1: Dependency satisfied (or no dependency)

    // Existing value wins
    if (hasRealValue) return f;

    const dv = (f as any).defaultValue;

    // Apply defaultValue if present
    if (dv !== null && typeof dv !== "undefined" && String(dv).trim() !== "") {
      return { ...f, value: toBoolean(dv) };
    }

    // Final fallback
    return { ...f, value: false };
  });
};



/**
 * Saves the prospect client data by submitting form fields to the API.
 *
 * @param setClientSaveApiProgress - Function to toggle loading state during the save process.
 * @param clientFormFields - Array of form fields filled in by the user.
 * @param setClientMasterId - Function to set the generated client master ID after save.
 * @param formUrl - Dynamic form endpoint URL.
 * @param formId - ID of the form being submitted.
 */
export const saveClientData = async (
  setClientSaveApiProgress: (arg: boolean) => void,
  clientFormFields: FieldProps[],
  setClientMasterId: (arg: number) => void,
  formUrl: string,
  formId: number,
  clientTypeCode: string,
  purpose: PurposeOption,
  activeTab: any,
  enableNextTab: (currentTab: any) => void,
  setShowSSNEINModal: (state: boolean) => void,
  setClientsMatchMessage: (message: any) => void,
  setClientsMatchedData: (data: any) => void,
  continueSavingClient: boolean,
  setShowMatchedSSNEINModal: (state: boolean) => void
) => {
  setClientSaveApiProgress(true);

  // 1) Build payload from the form
  const visibleFields = normalizeClientFieldValues(stripSyntheticRuleFields(clientFormFields));
  const normalizedForSave = applyCheckboxDefaultsBeforeSave(visibleFields);

  const formFields: FormField[] = buildFormFields(normalizedForSave);

  // LOS is already captured via the business_unit field in the form payload,
  // so no need to push a separate LOS marker here.

  try {
    const response = await makeCygnusApiCall({
      type: "POST",
      payload: {
        ClientFormFields: formFields,
        FormId: formId,
        ApprovalStatus: "prospect",
        ClientTypeCode: clientTypeCode,
        ContinueSavingClient: continueSavingClient
      },
      url: `/forms/${formUrl}/`,
      show_message: true,
      customized_success_message: "Prospect saved successfully.",
      customized_failure_message:
        "Unable to save your prospect, please try again. If the problem persists, please contact support and attach a screenshot.",
      customized_error_message:
        "Error occured while saving the prospect details, please contact support and attach a screenshot.",
      isblob: false,
      forMDM: true,
      skip_success_on_condition: (data: any) => data?.data?.match === true,
    });

    if (response.success) {
      if(response.data?.match) {
        // run modal
        const message = response.data?.matched_clients_message;
        if(message) {
          setClientsMatchMessage(message)
          setShowSSNEINModal(true)
        } 
        const matchedData = response.data?.matched_data;
        if(matchedData) { 
          setClientsMatchedData(matchedData)
          setShowMatchedSSNEINModal(true)
        }
        
      }
      else {
        const id = response.data?.[0]?.["ClientMasterId"];
        if (id) setClientMasterId(id);
        enableNextTab(activeTab);
        return id;
      }
      
    }
  } catch (error) {
    console.warn("Error occurred while saving client.");
  } finally {
    setClientSaveApiProgress(false);
  }
};

interface HandleClientSaveParams {
  activeTab: string;
  purpose: PurposeOption | null;
  clientMasterId: number;
  isCreate: boolean;
  setClientMasterId: (id: number) => void;
  formFieldsByTab: Record<string, FieldProps[]>;
  selectedFormByTab: Record<string, FormRecord | null>;
  setFormFieldsByTab: React.Dispatch<
    React.SetStateAction<Record<string, FieldProps[]>>
  >;
  setClientUpdateApiProgress: (state: boolean) => void;
  setClientDetails: (data: any) => void;
  setFetchClientApiProgress: {
    (value: React.SetStateAction<boolean>): void;
    (arg: boolean): void;
  };
  onHide?: () => void;
  enableNextTab: (currentTab: any) => void;
  setOnDemandValidation: (state: boolean) => void;
  setShowSSNEINModal: (state: boolean) => void;
  setClientsMatchMessage: (message: any) => void;
  setClientsMatchedData: (count: any) => void;
  continueSavingClient: boolean;
  setShowMatchedSSNEINModal: (state: boolean) => void;
}

export const handleClientSaveAndPrefill = async ({
  activeTab,
  purpose,
  isCreate,
  setClientMasterId,
  formFieldsByTab,
  selectedFormByTab,
  setFormFieldsByTab,
  setClientUpdateApiProgress,
  setClientDetails,
  setFetchClientApiProgress,
  clientMasterId,
  onHide,
  enableNextTab,
  setOnDemandValidation,
  setShowSSNEINModal,
  setClientsMatchMessage,
  setClientsMatchedData,
  continueSavingClient,
  setShowMatchedSSNEINModal
}: HandleClientSaveParams) => {
  const formUrl = selectedFormByTab[activeTab]?.Url ?? "";
  const formId = selectedFormByTab[activeTab]?.FirmFormId ?? 0;
  const fields = formFieldsByTab[activeTab] ?? [];
  const clientTypeCode = fields.find(
    (f) => f.allfields_key === "Client Type"
  )?.value;

  const isCreateMode = purpose?.label && isCreate;

  if (isCreateMode) {
    const createdClientId = await saveClientData(
      setClientUpdateApiProgress,
      fields,
      setClientMasterId,
      formUrl,
      formId,
      clientTypeCode,
      purpose,
      activeTab,
      enableNextTab,
      setShowSSNEINModal,
      setClientsMatchMessage,
      setClientsMatchedData,
      continueSavingClient,
      setShowMatchedSSNEINModal
    );

    if (createdClientId) {
      await prefillRemainingTabsFromClientDetails(
        createdClientId,
        activeTab,
        formFieldsByTab,
        selectedFormByTab,
        setFormFieldsByTab,
        setClientDetails,
        setFetchClientApiProgress
      );
    }
    setOnDemandValidation(false);
  } else {
    // update for all non-create tabs
    if (clientMasterId) {
      await updateClientData(
        setClientUpdateApiProgress,
        fields,
        clientMasterId,
        formUrl,
        formId,
        activeTab,
        enableNextTab
      );
      setOnDemandValidation(false);
    }
  }
};

export const prefillRemainingTabsFromClientDetails = async (
  clientMasterId: number,
  activeTab: string,
  formFieldsByTab: Record<string, FieldProps[]>,
  selectedFormByTab: Record<string, FormRecord | null>,
  setFormFieldsByTab: React.Dispatch<
    React.SetStateAction<Record<string, FieldProps[]>>
  >,
  setClientDetails: (data: any) => void,
  setFetchClientApiProgress: {
    (value: React.SetStateAction<boolean>): void;
    (arg: boolean): void;
  },
  cachedClientDetails?: any
) => {
  let latestClientDetails: any = cachedClientDetails ?? null;

  if (!latestClientDetails) {
    // Fetch and capture client details
    await fetchClientData(
      clientMasterId,
      (data) => {
      latestClientDetails = data;
      setClientDetails(data);
    },
    setFetchClientApiProgress
    );
  }
  if (!latestClientDetails) return;

  const tabsToPrefill = Object.keys(formFieldsByTab).filter(
    (tabKey) => tabKey !== activeTab
  );

  for (const tab of tabsToPrefill) {
    await prefillTabFields(
      tab,
      selectedFormByTab[tab],
      latestClientDetails,
      setFormFieldsByTab
    );
  }
};

const prefillTabFields = (
  tab: string,
  form: FormRecord | null,
  clientDetails: any,
  setFormFieldsByTab: React.Dispatch<
    React.SetStateAction<Record<string, FieldProps[]>>
  >
) => {
  if (!form?.InputStructure?.fields) return;

  const getClientFieldValue = (field: FieldProps) =>
    field.allfields_code
      ? clientDetails?.[field.allfields_code] ?? field.value
      : field.value;

  const prefilledFields = form.InputStructure.fields.map(
    (field: FieldProps) => ({
      ...field,
      value: getClientFieldValue(field),
    })
  );

  setFormFieldsByTab((prev) => ({
    ...prev,
    [tab]: prefilledFields,
  }));
};

export function patchFieldsWithClientData(
  fields: FieldProps[],
  clientData: Record<string, any>,
  opts?: {
    skip?: { byId?: string[]; byKey?: string[] };
    overwriteExisting?: boolean;
  }
): FieldProps[] {
  if (!clientData) return fields;

  const lower = (s: any) =>
    String(s ?? "")
      .toLowerCase()
      .trim();

  const skipId = new Set((opts?.skip?.byId ?? []).map(String));
  const skipKey = new Set((opts?.skip?.byKey ?? []).map(lower));

  return fields.map((f) => {
    const fKeys = [f.allfields_code, f.allfields_key, f.id, f.label].map(lower);

    if (skipId.has(String(f.id)) || fKeys.some((k) => skipKey.has(k))) return f;

    const fieldCode = f.allfields_code;
    const value = fieldCode ? clientData[fieldCode] : undefined;

    if (value === undefined) return f;
    
    if (!opts?.overwriteExisting) {
      const existing = f.value;
      if (existing !== undefined && existing !== null && existing !== "") {
        return f;
      }
    }

    return { ...f, value };
  });
}

export type DirtyApi = {
  get: () => string[];
  has: (id: string) => boolean;
  size: () => number;
  clear: () => void;
};

/**
 * Checks if a value represents a boolean (true/false),
 * including real booleans, stringified, or numeric equivalents.
 */
export const isBooleanLike = (val: any): boolean => {
  if (typeof val === "boolean") return true;
  if (typeof val === "string")
    return ["true", "false"].includes(val.toLowerCase());
  if (typeof val === "number") return val === 1 || val === 0;
  return false;
};

const normalizeBooleanLike = (v: any): any => {
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true") return true;
    if (s === "false") return false;
    return s;
  }

  if (typeof v === "number") {
    if (v === 1) return true;
    if (v === 0) return false;
    return v;
  }

  return v;
};
/**
 * Normalizes client form fields based on visibility rules.
 * - If field is hidden (show=false or dependency not satisfied), its value is cleared.
 * - Boolean-like values (true/false/"true"/"false"/1/0) are always preserved as-is.
 *
 * @param fields - Array of all client form fields
 * @returns Updated list of fields with invisible fields blanked out (except booleans)
 */
export const normalizeClientFieldValues = (fields: any[]) => {
  if (!Array.isArray(fields)) return [];

  return fields.map((f) => {
    let isVisible = true;

    if (isVisible && f.dependsOn) {
      const dependsOnField = fields.find(
        (dep) => dep.allfields_code === f.dependsOn
      );
      const dependsOnValue = dependsOnField?.value;

      if (
        Array.isArray(f.dependsOnVisibilityValues) &&
        f.dependsOnVisibilityValues.length > 0
      ) {
        const expected = f.dependsOnVisibilityValues.map(normalizeBooleanLike);
        if (Array.isArray(dependsOnValue)) {
          const values = dependsOnValue.map(normalizeBooleanLike);
          isVisible = expected.some((v:any) => values.includes(v));
        } else {
          isVisible = expected.includes(normalizeBooleanLike(dependsOnValue));
        }
      } else if (f.dependsOnVisibilityValue !== undefined && f.dependsOnVisibilityValue !== "") {
        isVisible = normalizeBooleanLike(f.dependsOnVisibilityValue) === normalizeBooleanLike(dependsOnValue);
      }
    }

    if (!isVisible && !isBooleanLike(f.value)) {
      return { ...f, value: "" };
    }

    return f;
  });
};


export const buildFormFields = (
  visibleFields: FieldProps[]
): FormField[] => {
  const formFields: FormField[] = [];

  visibleFields.forEach((field) => {
    formFields.push({
      code: field.allfields_code!,
      label: field.label,
      value: field.value ?? "",
    });

    if (field.type !== "select" || !field.value || !field.options?.length) {
      return;
    }

    const fieldValue = String(field.value);

    const optionMap = new Map(
      field.options.map((o) => [String(o.value), o])
    );

    const selectedOption = optionMap.get(fieldValue);
    if (!selectedOption) return;

    const meta = selectedOption as Record<string, any>;

    for (const key of ALLOWED_SELECT_META_KEYS) {
      const val = meta[key];
      if (val == null) continue;

      formFields.push({
        code: `${field.allfields_code}_${key}`,
        label: `${field.allfields_code}_${key}`,
        value: String(val),
      });
    }
  });

  return formFields;
};
